import {
  AuthSession,
  HiveRole,
  Permission,
  SessionUser,
} from './session.types';

const EMPTY_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>();

const SUPER_ADMIN_PERMISSIONS = new Set<Permission>([
  'clients:read',
  'clients:write',
  'events:read',
  'events:write',
  'events:publish',
  'program:read',
  'program:write',
  'program:approve',
  'rooms:write',
  'zones:write',
  'scanner:operate',
  'checkin:read',
  'push:compose',
  'push:send',
  'documents:write',
  'media:write',
  'surveys:write',
  'surveys:results',
  'campaigns:write',
  'broadcast:send',
  'reports:read',
  'theme:write',
  'auth:configure',
]);

const AGENCY_EDITOR_PERMISSIONS = new Set<Permission>([
  'clients:read',
  'clients:write',
  'events:read',
  'events:write',
  'events:publish',
  'program:read',
  'program:write',
  'program:approve',
  'rooms:write',
  'zones:write',
  'scanner:operate',
  'checkin:read',
  'push:compose',
  'push:send',
  'documents:write',
  'media:write',
  'surveys:write',
  'surveys:results',
  'campaigns:write',
  'broadcast:send',
  'reports:read',
  'theme:write',
]);

const CLIENT_EDITOR_PERMISSIONS = new Set<Permission>([
  'clients:read',
  'events:read',
  'events:write',
  'program:read',
  'program:write',
  'rooms:write',
  'zones:write',
  'checkin:read',
  'push:compose',
  'documents:write',
  'media:write',
  'surveys:write',
  'surveys:results',
  'campaigns:write',
  'reports:read',
  'theme:write',
]);

const CLIENT_VIEWER_PERMISSIONS = new Set<Permission>([
  'clients:read',
  'events:read',
  'program:read',
  'checkin:read',
  'surveys:results',
  'reports:read',
]);

const SCANNER_OPERATOR_PERMISSIONS = new Set<Permission>([
  'scanner:operate',
  'checkin:read',
]);

const ATTENDEE_PERMISSIONS = new Set<Permission>([
  'events:read',
  'program:read',
]);

export const ROLE_PERMISSIONS: ReadonlyMap<
  HiveRole,
  ReadonlySet<Permission>
> = new Map<HiveRole, ReadonlySet<Permission>>([
  ['super-admin', SUPER_ADMIN_PERMISSIONS],
  ['agency-editor', AGENCY_EDITOR_PERMISSIONS],
  ['client-editor', CLIENT_EDITOR_PERMISSIONS],
  ['client-viewer', CLIENT_VIEWER_PERMISSIONS],
  ['scanner-operator', SCANNER_OPERATOR_PERMISSIONS],
  ['attendee', ATTENDEE_PERMISSIONS],
]);

export function permissionsFor(
  user: SessionUser | null
): ReadonlySet<Permission> {
  if (!user || user.role === null) {
    return EMPTY_PERMISSIONS;
  }
  return ROLE_PERMISSIONS.get(user.role) ?? EMPTY_PERMISSIONS;
}

export function can(session: AuthSession, permission: Permission): boolean {
  return session.permissions().has(permission);
}

export function canAny(
  session: AuthSession,
  permissions: ReadonlyArray<Permission>
): boolean {
  const granted = session.permissions();
  return permissions.some((permission) => granted.has(permission));
}

export function canAll(
  session: AuthSession,
  permissions: ReadonlyArray<Permission>
): boolean {
  const granted = session.permissions();
  return permissions.every((permission) => granted.has(permission));
}
